// @givmo/board — what a legal hold covers.
//
// ══ THE REPORT IS THE BACKEND'S FACTS, ENUMERATED ══════════════════════════════════════════════
//
// This is the list an organization (or its counsel) hands to whoever asked for the hold. Everything on it
// is what the backend recorded and returned: the hold's own reason, who placed it and when, and the
// records it covers by identity, kind, state, the date each is about, and its content fingerprint.
// Nothing is composed here, and nothing states an effect — a hold that is standing is rendered as
// standing, and that is the whole claim.
//
// ⚠ THE COUNT IS SCOPED WITH THE LIST, AND IS NEVER COMPARED AGAINST ANYTHING. `covered_record_count`
// is the number of covered records THIS READER may see — an organization-wide hold also fences
// executive-session records the reader was not a participant of, and those are outside both the count
// and the list. So there is no "n more", no gap indicator, no row rendered as withheld, and no
// arithmetic between the count and the page length. Reporting a gap would disclose exactly what the
// record store withheld, which is the same rule the minute book itself obeys.
//
// The full page IS the signal that there may be more, which is a fact about the RESPONSE (it filled
// what was asked for) and not a claim about anything hidden.
//
// Since D-CBG-53 (2026-11-01) no officer screen lists the holds, so nothing in the console links here
// and the page has no way back to a list: it keeps its address, so a link someone kept still opens.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Fingerprint } from '../../components/fingerprint';
import { Link, useParams } from '../../board-routing';
import { type OrgBoardLegalHoldReportOut } from '@givmo/api-client';
import { Badge, Button, Card, CardBody, Skeleton } from '@givmo/design-system';
import { PageShell } from '../../components/page-shell';
import { useBoardHost } from '../../board-host';
import { recordKindLabel, recordStandingLabel, recordStateTone } from '../records/records-decode';
import {
  coverageLine,
  displayDate,
  displayInstant,
  holdScopeLine,
  holdStatusLabel,
  holdStatusTone,
  isActiveHold,
} from './retention-decode';
import { useBoardOperations } from '../../board-runtime';

/** How many covered records are asked for at a time. A FULL page offers more; a short one offers none. */
const PAGE_SIZE = 100;

export function HoldReportPage() {
  const operations = useBoardOperations();
  const { holdId } = useParams({ from: '/authenticated/legal-holds/$holdId' });
  const { orgRef, isReady } = useBoardHost();
  const [limit, setLimit] = useState(PAGE_SIZE);

  const reportQuery = useQuery({
    queryKey: ['board-legal-hold-report', orgRef, holdId, limit],
    enabled: isReady && orgRef !== null,
    queryFn: async (): Promise<OrgBoardLegalHoldReportOut> => {
      const { data } = await operations.officerGetBoardLegalHoldReport({
        path: { org_ref: orgRef ?? '', hold_id: holdId },
        query: { 'page[limit]': limit },
        throwOnError: true,
      });
      return data;
    },
  });

  const report = reportQuery.data ?? null;

  if (reportQuery.isPending) {
    return (
      <PageShell title="A legal hold" testId="page-hold-report">
        <Skeleton className="h-90 w-full rounded-lg" testId="hold-report-loading" />
      </PageShell>
    );
  }

  if (reportQuery.isError || report === null) {
    return (
      <PageShell title="A legal hold" testId="page-hold-report">
        <Card testId="hold-report-error">
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary">
              We couldn’t open this hold.
            </p>
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => void reportQuery.refetch()}
              testId="hold-report-retry"
            >
              Try again
            </Button>
          </CardBody>
        </Card>
      </PageShell>
    );
  }

  const hold = report.hold;
  const records = report.records ?? [];
  const mayBeMore = records.length >= limit;

  return (
    <PageShell title="A legal hold" description={holdScopeLine(hold)} testId="page-hold-report">
      <Card testId="hold-report-summary">
        <CardBody className="flex flex-col gap-8">
          <div className="flex flex-wrap items-center gap-8">
            <Badge tone={holdStatusTone(hold)}>{holdStatusLabel(hold)}</Badge>
          </div>

          <span
            className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary"
            data-testid="hold-report-reason"
          >
            {hold.reason}
          </span>

          <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
            Placed {displayInstant(hold.placed_at)}
          </span>

          {!isActiveHold(hold) ? (
            <span
              className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary"
              data-testid="hold-report-released"
            >
              Released {displayInstant(hold.released_at)}
              {hold.release_reason ? ` — ${hold.release_reason}` : ''}
            </span>
          ) : null}
        </CardBody>
      </Card>

      <Card testId="hold-report-records">
        <CardBody className="flex flex-col gap-16">
          <div className="flex flex-col gap-4">
            <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
              What it covers
            </h2>
            <span
              className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
              data-testid="hold-report-coverage"
            >
              {/* States what the number IS — what this reader may see — rather than presenting it as
                  the hold's absolute coverage. */}
              {coverageLine(report)}
            </span>
          </div>

          {records.length === 0 ? (
            <p
              className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
              data-testid="hold-report-empty"
            >
              There is nothing here for you to see.
            </p>
          ) : null}

          <ul className="flex flex-col gap-12">
            {records.map((record) => (
              <li
                key={record.record_id}
                className="flex flex-col gap-4 border-t border-border-subtle pt-12 first:border-t-0 first:pt-0"
                data-testid={`hold-report-record-${record.record_id}`}
              >
                <div className="flex flex-wrap items-center gap-8">
                  <Link
                    to="/records/$recordId"
                    params={{ recordId: record.record_id }}
                    className="font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
                    data-testid={`hold-report-record-link-${record.record_id}`}
                  >
                    {record.title}
                  </Link>
                  <Badge tone={recordStateTone(record.state)}>{recordStandingLabel(record)}</Badge>
                </div>
                <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                  {recordKindLabel(record.record_kind)}
                  {record.occurred_on ? ` · ${displayDate(record.occurred_on)}` : ''}
                </span>
                {record.content_hash ? (
                  // The WHOLE fingerprint (CBG-UX4): a shortened one cannot be compared.
                  <div className="flex flex-col gap-4 pt-2">
                    <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                      Its fingerprint
                    </span>
                    <Fingerprint
                      value={record.content_hash}
                      testId={`hold-report-hash-${record.record_id}`}
                      label={record.title}
                    />
                  </div>
                ) : null}
              </li>
            ))}
          </ul>

          {mayBeMore ? (
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => setLimit((current) => current + PAGE_SIZE)}
              testId="hold-report-more"
            >
              Show more
            </Button>
          ) : null}
        </CardBody>
      </Card>
    </PageShell>
  );
}
